/**
 * layout.js — El plano de una skin de 64×64.
 *
 * Es el formato estándar de Minecraft, y se respeta a propósito: así una skin
 * hecha aquí se puede exportar y usar en cualquier otra parte, y una traída de
 * fuera encajaría sin tocar nada.
 *
 * Las coordenadas son de CANVAS (y hacia abajo). El paso a UV —donde la v va
 * hacia arriba— lo hace uv.js, que es el único sitio que conoce esa vuelta.
 *
 * El orden de las seis caras es el de BoxGeometry de Three.js:
 *   +X derecha · -X izquierda · +Y arriba · -Y abajo · +Z frente · -Z espalda
 *
 * «Derecha» es la del PERSONAJE, no la de quien mira: por eso la manga derecha
 * queda a la izquierda de la pantalla cuando te mira de frente.
 */

export const TAM = 64;

/** Las seis caras de una caja, a partir de su esquina y sus medidas en píxeles.
 *
 *  El despliegue es el clásico: arriba una franja de `fondo` de alta con la
 *  tapa y la base, y debajo la tira de los cuatro costados.
 *
 *      ┌──┬──┐
 *      │ar│ab│          ← franja superior, de `fondo` de alta
 *   ┌──┼──┼──┼─────┐
 *   │de│fr│iz│espal│    ← los cuatro costados, de `alto` de altos
 *   └──┴──┴──┴─────┘
 */
function caja(x, y, ancho, alto, fondo) {
  const yl = y + fondo;                 // los costados van bajo la franja
  return {
    arriba:    [x + fondo,             y,  ancho, fondo],
    abajo:     [x + fondo + ancho,     y,  ancho, fondo],
    derecha:   [x,                     yl, fondo, alto],
    frente:    [x + fondo,             yl, ancho, alto],
    izquierda: [x + fondo + ancho,     yl, fondo, alto],
    espalda:   [x + fondo * 2 + ancho, yl, ancho, alto],
  };
}

/* Las medidas son las canónicas: cabeza 8³, torso 8×12×4, brazos y piernas
   4×12×4. El «alto» que se le pasa a caja() es el de la cara lateral. */
export const PARTES = {
  cabeza:        { ...caja(0,  0,  8, 8,  8), ancho: 8, alto: 8,  fondo: 8 },
  torso:         { ...caja(16, 16, 8, 12, 4), ancho: 8, alto: 12, fondo: 4 },
  brazoDerecho:  { ...caja(40, 16, 4, 12, 4), ancho: 4, alto: 12, fondo: 4 },
  brazoIzquierdo:{ ...caja(32, 48, 4, 12, 4), ancho: 4, alto: 12, fondo: 4 },
  piernaDerecha: { ...caja(0,  16, 4, 12, 4), ancho: 4, alto: 12, fondo: 4 },
  piernaIzquierda:{ ...caja(16, 48, 4, 12, 4), ancho: 4, alto: 12, fondo: 4 },
};

/* La segunda capa. En Minecraft es la que hace el pelo, el casco, la capucha,
   las orejas y la chaqueta: va 0,5 px por fuera del cuerpo y admite
   transparencia, así que se puede añadir volumen sin deformar el modelo. */
export const CAPA2 = {
  cabeza:        { ...caja(32, 0,  8, 8,  8), ancho: 8, alto: 8,  fondo: 8 },
  torso:         { ...caja(16, 32, 8, 12, 4), ancho: 8, alto: 12, fondo: 4 },
  brazoDerecho:  { ...caja(40, 32, 4, 12, 4), ancho: 4, alto: 12, fondo: 4 },
  brazoIzquierdo:{ ...caja(48, 48, 4, 12, 4), ancho: 4, alto: 12, fondo: 4 },
  piernaDerecha: { ...caja(0,  32, 4, 12, 4), ancho: 4, alto: 12, fondo: 4 },
  piernaIzquierda:{ ...caja(0,  48, 4, 12, 4), ancho: 4, alto: 12, fondo: 4 },
};

export const CARAS = ['derecha', 'izquierda', 'arriba', 'abajo', 'frente', 'espalda'];

/** Medidas del modelo en bloques. Un bloque son 16 px de skin, y el personaje
 *  mide 32 px de alto = 2 bloques, que es justo el hueco por el que cabe. */
export const ESCALA = 1 / 16;
